import React, { useEffect, useState }                                   from 'react';
import { useCurrentUser, usePrefStates }                                from "@src/hooks";
import { DialogIntroductionContainer }                                  from "@src/views/containers/DialogIntroductionContainer";
import { CorvalOrganization, CorvalProject, CorvalUIColor, Size, Uuid } from "@src/types";
import { Checkbox, CheckboxProps, Divider, Header, List, Segment }      from "semantic-ui-react";
import { ENTITY_ICONS }                                                 from "@src/constants";
import _                                                                from "underscore";
import { useQuery }                                                     from "@apollo/react-hooks";
import { SystemAccountingQueries }                                      from "@src/queries";
import { queriesHaveData, queriesLoading }                              from "@src/store/_apolloUtil";
import { StyledContainer }                                              from "@src/views/containers";
import { BigGearLoadingMessage }                                        from "@src/views/components/util/loading/BigGearLoadingMessage";
import { EmptyOrganizationList }                                        from "@src/views/pages/projects/overviews/project-dashboard/EmptyOrganizationList";
import { Cell, Grid }                                                   from "styled-css-grid";
import { zipArraysOddEven }                                             from "@src/util";
import { TextLink }                                                     from "@src/views/components/display/TextLink";


interface ProjectsAndOrganizationInfo {
    organization: Partial<CorvalOrganization>,
    projects: CorvalProject[],
}

interface GlobalProjectVisibilitySettingsControlProps {}

const GlobalProjectVisibilitySettingsControlDefaultProps = {} satisfies Partial<GlobalProjectVisibilitySettingsControlProps>;

function GlobalProjectVisibilitySettingsControl( _props: GlobalProjectVisibilitySettingsControlProps ){

    const props = { ...GlobalProjectVisibilitySettingsControlDefaultProps, ..._props };

    let { currentUser }                           = useCurrentUser(),
        [ , prefs, prefEnums ]                    = usePrefStates(),
        [ projectsByOrg, setProjectsByOrg ]       = useState<ProjectsAndOrganizationInfo[]>(),
        [ selectedProjects, setSelectedProjects ] = useState<Uuid[]>();

    const projectListingQueryReq = useQuery( SystemAccountingQueries.queries.GET_PROJECTS( `id name organization{ id name }` ), {
        variables:   {
            where: {
                id:       { in: _.pluck( currentUser.projects, 'projectId' ) },
                isActive: { eq: true },
            },
            order: [ { name: 'ASC' } ]
        },
        fetchPolicy: 'cache-and-network'
    } );

    const isLoading = queriesLoading( projectListingQueryReq );

    useEffect( () => {

        if( !queriesLoading( projectListingQueryReq ) && queriesHaveData( projectListingQueryReq ) ){

            let { projects } = projectListingQueryReq.data;

            let projectsByOrg = _.chain( projects )
                                 .groupBy( project => project.organization.name )
                                 .pairs()
                                 .sortBy( ( [ orgName, ] ) => orgName )
                                 .map( ( [ , set ] ) => ( {
                                     organization: _.first( set ).organization,
                                     projects:     set,
                                 } ) )
                                 .value();

            setProjectsByOrg( projectsByOrg );
        }

    }, [ projectListingQueryReq ] );

    useEffect( () => {

        let projectIds = prefs.get( prefEnums.GLOBAL_ACTIVE_PROJECT_VISIBILITY ) as Uuid[];

        // If none selected, default to all.
        if( _.isEmpty( projectIds ) ){

            if( queriesHaveData( [ projectListingQueryReq ] ) ){
                projectIds = _.pluck( projectListingQueryReq.data.projects, 'id' );
            }
        }

        setSelectedProjects( projectIds );

    }, [ projectsByOrg ] );

    useEffect( () => {


    }, [ selectedProjects ] );

    const commitUpdate = ( projectIds: Uuid[] ) => {

        // save to pref state
        prefs.update( prefEnums.GLOBAL_ACTIVE_PROJECT_VISIBILITY, projectIds );

        // update local state
        setSelectedProjects( projectIds );
    };

    const handleProjectToggled = ( event: React.MouseEvent<HTMLElement>, data: CheckboxProps ) => {

        const projectIds = ( data.checked )
                           ? _.union( selectedProjects, [ data.value ] )
                           : _.without( selectedProjects, data.value );

        commitUpdate( projectIds );
    };

    const handleAddProjects = ( projects: CorvalProject[] ) => {

        const projectIds = _.union( selectedProjects, projects.map( project => project.id ) );

        commitUpdate( projectIds );

    };

    const handleRemoveProjects = ( projects: CorvalProject[] ) => {

        const projectIds = _.difference( selectedProjects, projects.map( project => project.id ) );

        commitUpdate( projectIds );
    };

    return (

        <Segment color={ 'black' }>

            <DialogIntroductionContainer
                containerProps={ { padding: [ Size.SM, null ], bgColor: CorvalUIColor.WHITE, } }
                icon={ ENTITY_ICONS.PROJECTS.ENTITY }
                title={ 'Project Visibility' }
                description={ 'Choose the projects you want to have visible in the platform. Unchecked projects will be hidden from view, but your access to them will not change.' }
            />

            { isLoading && (

                <StyledContainer padding={ Size.XL }>

                    <BigGearLoadingMessage label={ 'Loading Project Data...' } active={ true }/>

                </StyledContainer>

            ) }

            { !isLoading && projectsByOrg && (
                <Segment secondary style={ {
                    maxHeight: 400,
                    overflowY: 'auto',
                } }>

                    { ( projectsByOrg.length === 0 )
                      ? <EmptyOrganizationList/>
                      : <Grid columns={ 1 }>

                          { projectsByOrg.map( ( org, index ) => {

                              let projects = org.projects;

                              const hasMulitpleProjects = ( org.projects.length > 1 );

                              if( hasMulitpleProjects ){

                                  const partitionedProjects = _.chain( org.projects )
                                                               .sortBy( project => project.name )
                                                               .chunk( Math.ceil( org.projects.length / 2 ) )
                                                               .value() as [ CorvalProject[], CorvalProject[] ];

                                  projects = zipArraysOddEven( partitionedProjects[ 0 ], partitionedProjects[ 1 ] );
                              }

                              return (
                                  <Cell key={ index }>
                                      <Segment raised>

                                          <Grid columns={ 2 } style={ { width: '100%' } }>
                                              <Cell>
                                                  <Header size={ 'small' }>{ org.organization.name }</Header>
                                              </Cell>
                                              <Cell>
                                                  <List horizontal divided floated={ 'right' }>
                                                      <List.Item><TextLink onClick={ () => { handleAddProjects( projects ); } }>All</TextLink></List.Item>
                                                      <List.Item><TextLink onClick={ () => { handleRemoveProjects( projects ); } }>None</TextLink></List.Item>
                                                  </List>
                                              </Cell>
                                          </Grid>
                                          <Divider/>
                                          <Grid columns={ 2 }>
                                              { projects.map( ( project: CorvalProject ) => (

                                                  <Cell key={ project.id } middle>
                                                      <Checkbox value={ project.id } checked={ selectedProjects.includes( project.id ) } label={ project.name } onClick={ handleProjectToggled }/>
                                                  </Cell>
                                              ) ) }
                                          </Grid>

                                      </Segment>

                                  </Cell>
                              );

                          } ) }
                      </Grid>
                    }

                </Segment>
            ) }

        </Segment>
    );
}

export {
    GlobalProjectVisibilitySettingsControl,
    GlobalProjectVisibilitySettingsControlDefaultProps,
    type GlobalProjectVisibilitySettingsControlProps,
};
